// Gedeelde dialooglogica voor Modal en MenuDrawer (AGENTS.md §8):
// - focus naar binnen bij openen (initialFocusRef > autoFocus-element > eerste focusbare element)
// - Tab-focusval, focus terug naar het vorige element bij sluiten
// - scroll-lock op <body> (met teller, zodat geneste dialogen elkaar niet ontgrendelen)
// - stapelveilige Escape: alleen de bovenste dialoog sluit
import { useEffect, useRef, type RefObject } from 'react';
import { nextFocusIndex } from './focusTrap';

export interface UseDialogOptions {
  /** Dialoog is open (bij een uitloop-animatie: false zodra het sluiten begint). */
  active: boolean;
  /** Aangeroepen bij Escape (alleen als deze dialoog bovenop ligt). */
  onClose: () => void;
  /** Het dialoogpaneel: focus blijft hierbinnen. */
  containerRef: RefObject<HTMLElement | null>;
  /** Optioneel: element dat bij openen focus krijgt. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Standaard true. */
  lockScroll?: boolean;
}

interface StackEntry {
  container: RefObject<HTMLElement | null>;
  onClose: RefObject<() => void>;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

const stack: StackEntry[] = [];
let scrollLocks = 0;
let savedOverflow = '';

export function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getClientRects().length > 0,
  );
}

function onKeyDown(e: KeyboardEvent) {
  const top = stack[stack.length - 1];
  const container = top?.container.current;
  if (!top || !container) return;

  if (e.key === 'Escape') {
    if (e.defaultPrevented) return;
    e.preventDefault();
    e.stopPropagation();
    top.onClose.current?.();
    return;
  }
  if (e.key !== 'Tab') return;

  const focusables = getFocusable(container);
  const active = document.activeElement as HTMLElement | null;
  const current = active ? focusables.indexOf(active) : -1;
  const inside = !!active && container.contains(active);
  const next = nextFocusIndex(focusables.length, current, e.shiftKey, inside);
  if (next === null) return;
  e.preventDefault();
  if (next === -1) container.focus();
  else focusables[next].focus();
}

function push(entry: StackEntry) {
  if (stack.length === 0) document.addEventListener('keydown', onKeyDown, true);
  stack.push(entry);
}

function remove(entry: StackEntry) {
  const i = stack.indexOf(entry);
  if (i !== -1) stack.splice(i, 1);
  if (stack.length === 0) document.removeEventListener('keydown', onKeyDown, true);
}

function lockBodyScroll() {
  if (scrollLocks++ === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
}

function unlockBodyScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.body.style.overflow = savedOverflow;
}

export function useDialog({ active, onClose, containerRef, initialFocusRef, lockScroll = true }: UseDialogOptions) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!active) return;
    const entry: StackEntry = { container: containerRef, onClose: onCloseRef };
    const previouslyFocused = document.activeElement as HTMLElement | null;
    push(entry);
    if (lockScroll) lockBodyScroll();

    const container = containerRef.current;
    if (container) {
      const preferred = initialFocusRef?.current;
      if (preferred) {
        preferred.focus({ preventScroll: true });
      } else if (!container.contains(document.activeElement)) {
        // Een kind met autoFocus heeft de focus al gepakt; anders het eerste focusbare element.
        const first = getFocusable(container)[0];
        (first ?? container).focus({ preventScroll: true });
      }
    }

    return () => {
      remove(entry);
      if (lockScroll) unlockBodyScroll();
      if (previouslyFocused && document.contains(previouslyFocused)) {
        try {
          previouslyFocused.focus({ preventScroll: true });
        } catch {
          /* negeren */
        }
      }
    };
    // containerRef/initialFocusRef zijn stabiele refs; alleen openen/sluiten telt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, lockScroll]);
}
